import * as React from 'react';

import { ServiceLocator } from '../../app/ServiceLocator';
import { Messages } from '../../codegen/Localize';
import { CommonStyles } from '../../common/react/CommonStyles';
import { InterpolatedString } from '../../common/react/components/InterpolatedString';
import { cssClass, fillStyle, flexColumnStyle } from '../../style';
import { ServerFilterClauses } from '../../ts/helpers/ServerFilterClauses';
import { SliceableHelper } from '../../ts/helpers/SliceableHelper';
import { FlowOutputStepInfo } from '../../ts/models/FlowOutputStepInfo';
import { FlowOutputStepRunListItemInfo } from '../../ts/models/FlowOutputStepRunInfo';
import { RunFlowTaskInfo } from '../../ts/models/RunFlowTaskInfo';
import { Set } from '../../ts/models/Set';
import { FlowOutputStepRuns } from '../../ts/resources/FlowOutputStepRuns';
import { Flows } from '../../ts/resources/Flows';
import { RunFlowTasks } from '../../ts/resources/RunFlowTasks';
import { ChangeFlowsTagsAction } from '../../ts/services/ChangeFlowsTagsAction';
import { HelpService } from '../../ts/services/HelpService';
import { ServerService } from '../../ts/services/ServerService';
import { grey230border } from '../styles/FlowStyleConstants';
import { FlowSubplaceProps } from '../types/FlowSubplaceProps';
import { FlowRunEvent } from '../utils/FlowEvents';
import { FlowDescription } from './FlowDescription';
import { FlowOverviewDataGridView } from './FlowOverviewDataGridView';
import { FlowOverviewFlowGraphImage } from './FlowOverviewFlowGraphImage';
import { FlowTags } from './FlowTags';

const cssPrefix = 'FlowOverviewSubplace';

const rootClass = cssClass(cssPrefix, 'root', fillStyle, {
  overflow: 'auto'
});

const warningBannerClass = cssClass(cssPrefix, 'warningBanner', flexColumnStyle, {
  flex: '1',
  border: grey230border,
  backgroundColor: 'rgb(247, 199, 203)',
  padding: CommonStyles.Sizes.LargeSpace,
});

const boldText = cssClass(cssPrefix, 'boldText', {
  fontWeight: 'bold'
});

const contentClass = cssClass(cssPrefix, 'content', {
  padding: `${CommonStyles.Sizes.MediumSpace}px ${CommonStyles.Sizes.MediumSpace}px ${CommonStyles.Sizes.MediumSpace}px ${CommonStyles.Sizes.LargeSpace}px`
});

export function flowHasIncompatibleDocumentVersion(subplaceProps: FlowSubplaceProps): boolean {
  // (mechen) flow with incompatible doc version will have their file type as NA, since we cant open/read it.
  return subplaceProps.flowDetailInfo.flowFileType && subplaceProps.flowDetailInfo.flowFileType === 'na';
}

export type FlowOverviewSubplaceState = {
  readonly outputStepInfos: ReadonlyArray<FlowOutputStepInfo>;
  readonly outputStepRuns: ReadonlyArray<FlowOutputStepRunListItemInfo>;
  readonly runFlowTasks: ReadonlyArray<RunFlowTaskInfo>;
};

export class FlowOverviewSubplace extends React.Component<FlowSubplaceProps, FlowOverviewSubplaceState> {

  private flows: Flows;
  private flowOutputStepRuns: FlowOutputStepRuns;
  private runFlowTasks: RunFlowTasks;
  private serverService: ServerService;
  private helpService: HelpService;
  private changeFlowsTagsAction: ChangeFlowsTagsAction;
  private scope: ng.IScope;
  private unmounted: boolean = false;

  constructor(props: FlowSubplaceProps) {
    super(props);
    this.flows = ServiceLocator.get('Flows');
    this.flowOutputStepRuns = ServiceLocator.get('FlowOutputStepRuns');
    this.runFlowTasks = ServiceLocator.get('RunFlowTasks');
    this.serverService = ServiceLocator.get('ServerService');
    this.helpService = ServiceLocator.get('HelpService');
    this.changeFlowsTagsAction = ServiceLocator.get('ChangeFlowsTagsAction');

    this.state = {
      outputStepRuns: null,
      outputStepInfos: null,
      runFlowTasks: null
    };
    this.fetchData();

    this.scope = this.props.angularContext.scope.$new();
  }

  private fetchData = () => {
    if (!this.unmounted) {
      this.fetchOutputMostRecentRuns();
      this.fetchOutputStepInfo();
      this.fetchRunFlowTasks();
    }
  }

  private fetchOutputMostRecentRuns = () => {
    if (!this.unmounted) {
      this.flowOutputStepRuns.getMostRecentForFlow(this.props.flowDetailInfo.id, false).then((runs) => {
        this.setState({outputStepRuns: runs});
      });
    }
  }

  private fetchOutputStepInfo = () => {
    if (!this.unmounted) {
      this.flows.getOutputStepInfos(this.props.flowDetailInfo.id).then((outputStepInfos) => {
        this.setState({outputStepInfos: outputStepInfos});
      });
    }
  }

  private fetchRunFlowTasks = () => {
    if (!this.unmounted) {
      const runFlowTasks = this.runFlowTasks.all().addFilter(ServerFilterClauses.serverFilterClauses.flow(this.props.flowDetailInfo.id));
      SliceableHelper.fetchAll(runFlowTasks).then(tasks => {
        this.setState({runFlowTasks: tasks.items});
      });
    }
  }

  private refreshData = () => {
    if (!this.unmounted) {
      this.props.refreshData();
    }
  }

  private editTags = () => {
    const singleton = Set.singletonForIdItem(this.props.flowDetailInfo);
    const context = ServiceLocator.getAngularActionContext();
    this.changeFlowsTagsAction.execute(singleton, context).then(() => this.refreshData());
  }

  private renderDescription = (): JSX.Element => {
    return this.props.flowAllowedActions.setDescription || this.props.flowDetailInfo.description ? (
      <div className={contentClass}>
        <FlowDescription
          flowDetailInfo={this.props.flowDetailInfo}
          editDescriptionAllowed={this.props.flowAllowedActions.setDescription}
          refreshData={this.refreshData}
          sanitizer={this.props.sanitizer}
        />
      </div>
    ) : null;
  }

  private renderTags = (): JSX.Element => {
    return this.props.flowAllowedActions.setTags || this.props.flowDetailInfo.tags.length ? (
      <div className={contentClass}>
        <FlowTags
          editTagAllowed={this.props.flowAllowedActions.setTags}
          editTags={this.editTags}
          siteUrls={this.props.siteUrls}
          tags={this.props.flowDetailInfo.tags}
        />
      </div>
    ) : null;
  }

  public componentDidMount() {
    this.scope.$on(FlowRunEvent, () => {
      if (!this.unmounted) {
        this.fetchData();
      }
    });
  }

  public componentDidUpdate(prevProps: FlowSubplaceProps) {
    if (prevProps.flowDetailInfo.id !== this.props.flowDetailInfo.id) {
      this.fetchData();
    }
  }

  public componentWillUnmount() {
    this.scope.$destroy();
    this.unmounted = true;
  }

  private buildIncompatibleDocumentVersionHelpURL(): string {
    const helpRedirectToken: string = 'prep-conductor-compatibility';
    const helpEdition: string = 'prep';
    const documentVersion = this.props.flowDetailInfo.flowDocumentVersion;

    return this.helpService.getHelpUrlByCategory(helpRedirectToken, helpEdition, documentVersion);
  }

  private renderIncompatibleDocumentVersionBanner(): JSX.Element {
    const learnMoreLink = <a href={this.buildIncompatibleDocumentVersionHelpURL()}>{Messages.learnMore()}</a>;
    return (
      <div className={warningBannerClass}>
        <div>
          <InterpolatedString
            baseStringKey="flowOverviewIncompatibleDocumentVersionWarningLine1"
            translate={this.props.translate}
            replacements={{
              PREP_VERSION: () => <span className={boldText}>{Messages.TableauPrepVersionNumber({PREP_VERSION: this.props.flowDetailInfo.flowDocumentVersion})}</span>,
              SERVER_VERSION: () => <span className={boldText}>{Messages.TableauServerVersionNumber({SERVER_VERSION: this.helpService.externalVersion()})}</span>
            }}
          />
        </div>
        <div>
          {Messages.flowOverviewIncompatibleDocumentVersionWarningLine2() + ' '}
          {learnMoreLink}
        </div>
      </div>
    );
  }

  public render(): JSX.Element {
    return (
      <div className={rootClass}>
        { this.renderDescription() }
        { flowHasIncompatibleDocumentVersion(this.props) ?
          this.renderIncompatibleDocumentVersionBanner() :
          this.state.outputStepInfos && this.state.runFlowTasks  && this.state.outputStepRuns ?
          <FlowOverviewDataGridView
            flowInfo={this.props.flowDetailInfo}
            updateData={this.fetchData}
            siteUrls={this.props.siteUrls}
            outputStepInfos={this.state.outputStepInfos}
            outputStepRuns={this.state.outputStepRuns}
            runFlowTasks={this.state.runFlowTasks}
            flowAllowedActions={this.props.flowAllowedActions}
            isServerOrSiteAdmin={this.serverService.isSessionSiteOrServerAdmin()}
          /> : null
        }
        <FlowOverviewFlowGraphImage flowGraphImageUrl={this.props.flowDetailInfo.flowGraphImageUrl}/>
        { this.renderTags() }
      </div>
    );
  }
}
